import { sendMessageAsync } from '@adloid-technologies/discover-sdk/dist/esm/graphics-utils/messages';
import { returnSkuForID } from '@adloid-technologies/discover-sdk/dist/esm/graphics-utils/sku';

/**
 * A `basic-light` interaction that sends the SAME message name for both
 * start and stop (e.g. TAILLIGHT_GLOW/TAILLIGHT_GLOW, matching HEADLIGHT_GLOW
 * and DRL_GLOW) relies on the 3D asset's own material reacting to that one
 * message - fine for headlight/DRL, but taillight's material needs an
 * explicit colour swap to actually look "on" vs "off" (brake-red vs a dim
 * off-state, rather than a single hardcoded glow colour).
 *
 * 1.0 covered this with a `glowMesh` field (materialName + on/off RGB) sent
 * via a `set-material-properties` engine message (see
 * metadome-autodome-web/src/utils/integration.ts `glowByMesh`). That engine
 * message already exists and works today - 1.0 and 2.0 both load the exact
 * same remote graphics engine (gsdk-internal.metadome.ai) - it was just never
 * wired up on the 2.0 side. This ports that one function; nothing on the
 * engine or SDK side needs to change.
 */
export interface IGlowMesh {
  materialName: string;
  identifiers: string[];
  glow_on: number[];
  glow_off: number[];
}

export function applyGlowMesh(glowMesh: IGlowMesh | undefined, glowState: boolean): Promise<unknown> {
  if (!glowMesh) return Promise.resolve();
  return Promise.all(
    glowMesh.identifiers.map((identifier) => {
      const sku = returnSkuForID(identifier);
      if (!sku) return Promise.resolve();
      return sendMessageAsync({
        eventType: 'set-material-properties',
        data: {
          assetIdentifier: sku.assetIdentifier,
          materialName: glowMesh.materialName,
          albedoColor: glowState ? glowMesh.glow_on : glowMesh.glow_off,
        },
      } as any);
    }),
  );
}
